import { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import CompaniesTable from './CompaniesTable';
import AddCompanyModal from './AddCompanyModal';
import EditCompanyModal from './EditCompanyModal';
import AdminCredentialsModal from './AdminCredentialsModal';
import DeleteCompanyModal from './DeleteCompanyModal';
import PageHeader from '../../../components/PageHeader/PageHeader';
import { useTranslation } from 'react-i18next';
import CompanyService from '../../../services/CompanyService';
import SnackbarUtils from '../../../utils/SnackbarUtils';

const CompaniesPage = () => {
  const { t } = useTranslation();

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [credentialsCompany, setCredentialsCompany] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCredentialsModalOpen, setIsCredentialsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteCompany, setDeleteCompany] = useState(null);
  const [deletePlan, setDeletePlan] = useState(null);
  const [deletePlanLoading, setDeletePlanLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await CompanyService.getCompanies(1, 100000, '');
      // Asegurarse de que siempre sea un array
      setCompanies(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleAddCompany = async (newCompany) => {
    try {
      const createdCompany = await CompanyService.createCompany(newCompany);
      // Asegurar que la respuesta sea un objeto válido con id
      if (createdCompany && typeof createdCompany === 'object' && createdCompany.id) {
        setCompanies(prev => [...prev, createdCompany]);
      } else {
        // Si la respuesta no es válida, refrescar desde el servidor
        await fetchCompanies();
      }
      setIsAddModalOpen(false);
    } catch (error) {
      console.error('Error creating company:', error);
      throw error;
    }
  };
  
  const handleEditCompany = async (updatedCompany) => {
    try {
      const updatedCompanyData = await CompanyService.updateCompany(updatedCompany.id, updatedCompany);
      // Asegurar que la respuesta sea un objeto válido, no un booleano
      if (updatedCompanyData && typeof updatedCompanyData === 'object' && updatedCompanyData.id) {
        setCompanies(prev =>
          prev.map(company => (company.id === updatedCompany.id ? updatedCompanyData : company))
        );
      } else {
        // Si la respuesta no es válida, refrescar desde el servidor
        await fetchCompanies();
      }
      setIsEditModalOpen(false);
      setSelectedCompany(null);
    } catch (error) {
      console.error('Error updating company:', error);
      throw error;
    }
  };

  const handleOpenDeleteCompany = async (company) => {
    setDeleteCompany(company);
    setDeletePlan(null);
    setIsDeleteModalOpen(true);
    setDeletePlanLoading(true);
    try {
      const plan = await CompanyService.getCompanyDeletionPlan(company.id);
      setDeletePlan(plan);
    } catch (error) {
      console.error('Error fetching company deletion plan:', error);
      SnackbarUtils.error(error?.response?.data?.error_message || 'No se pudo cargar el detalle de eliminación');
      setIsDeleteModalOpen(false);
      setDeleteCompany(null);
    } finally {
      setDeletePlanLoading(false);
    }
  };

  const handleCloseDeleteCompany = () => {
    setIsDeleteModalOpen(false);
    setDeleteCompany(null);
    setDeletePlan(null);
    setDeletePlanLoading(false);
  };

  const handleDeleteCompany = async (company) => {
    try {
      await CompanyService.deleteCompany(company.id);
      setCompanies(prev => prev.filter(item => item.id !== company.id));
      handleCloseDeleteCompany();
      SnackbarUtils.success('Empresa eliminada correctamente');
    } catch (error) {
      console.error('Error deleting company:', error);
      SnackbarUtils.error(error?.response?.data?.error_message || 'No se pudo eliminar la empresa');
      throw error;
    }
  };

  return (
    <Box p={3}>
      <PageHeader title={t('companies')} subtitle={t('manage_companies')} />
      <CompaniesTable
        loading={loading}
        companies={companies}
        onAdd={() => setIsAddModalOpen(true)}
        onRefresh={fetchCompanies}
        onEdit={(company) => {
          setSelectedCompany(company);
          setIsEditModalOpen(true);
        }}
        onRevealCredentials={(company) => {
          setCredentialsCompany(company);
          setIsCredentialsModalOpen(true);
        }}
        onDelete={handleOpenDeleteCompany}
      />
      <AddCompanyModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleAddCompany}
      />
      <EditCompanyModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleEditCompany}
        company={selectedCompany}
      />
      <AdminCredentialsModal
        open={isCredentialsModalOpen}
        onClose={() => setIsCredentialsModalOpen(false)}
        company={credentialsCompany}
      />
      <DeleteCompanyModal
        open={isDeleteModalOpen}
        onClose={handleCloseDeleteCompany}
        onDelete={handleDeleteCompany}
        company={deleteCompany}
        deletionPlan={deletePlan}
        loadingPlan={deletePlanLoading}
      />
    </Box>
  );
};

export default CompaniesPage;
